import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { BudgetDepartmentModal } from '../components/BudgetDepartmentModal';
import { budgetDepartmentsApi } from '../budgetDepartmentsApi';
import type { BudgetDepartmentListItem } from '../budgetDepartmentTypes';

/**
 * Which organizational units propose a budget, reached through **Procurement → Investment
 * Planning → Settings**.
 *
 * It is the DENOMINATOR on every plan's roster: the plan lists one row per enrolled
 * department, whether or not it has proposed anything, and states how many have started out
 * of how many there are. An explicit list rather than a rule over the chart's tiers — see
 * `BudgetDepartment` for why.
 */
export function BudgetDepartmentsIndexPage() {
  const navigate = useNavigate();

  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    department: BudgetDepartmentListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchDepartments = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      budgetDepartmentsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  /*
    NO Edit entry, and the absence is the design: the row holds a link to an org unit and
    nothing else, so there is nothing on it to correct. Moving the roster to a different
    office is a withdrawal and an enrolment, which are the two entries this menu has.
  */
  const rowActions = useCallback(
    (department: BudgetDepartmentListItem): MenuItem[] => [
      {
        key: `restore-${department.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'budget-departments.delete',
        disabledReason: department.isActive ? 'This department is already enrolled.' : null,
        onClick: () => setConfirming({ kind: 'activate', department }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') {
        await budgetDepartmentsApi.deactivate(confirming.department.id);
      } else {
        await budgetDepartmentsApi.activate(confirming.department.id);
      }

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<BudgetDepartmentListItem>
        module="budget-departments"
        breadcrumb={[
          'Procurement',
          'Investment Planning',
          { label: 'Settings', to: '/procurement/investment-planning/settings' },
          'Budget Departments',
        ]}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={['BudgetDepartment']}
        rowKey={(department) => department.id}
        fetch={fetchDepartments}
        reloadKey={reloadKey}
        searchPlaceholder="Search departments"
        emptyMessage="No budget departments yet. Enrol the offices that propose a budget — every plan's roster is built from this list."
        onCreate={() => setCreating(true)}
        createPermission="budget-departments.create"
        onRowOpen={(department) =>
          navigate(`/procurement/investment-planning/settings/budget-departments/${department.id}`)
        }
        openPermission="budget-departments.read"
        rowActions={rowActions}
        onDelete={(department) => setConfirming({ kind: 'deactivate', department })}
        deletePermission="budget-departments.delete"
        deleteLabel={() => 'Withdraw'}
        deleteDisabledReason={(department) =>
          department.isActive ? null : 'This department is already withdrawn.'
        }
        onClearData={() => budgetDepartmentsApi.clear()}
        clearDataPermission="budget-departments.clear-data"
        otherInfo={(department) => department}
      />

      {creating ? (
        <BudgetDepartmentModal
          open
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Withdraw department' : 'Restore department'}
          danger={confirming.kind === 'deactivate'}
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`budget-department-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `${confirming.department.orgUnitName} stops appearing on every plan's roster, and stops counting toward how many departments are expected to propose.`
                  : `${confirming.department.orgUnitName} appears on every plan's roster again.`}
              </p>

              {/*
                The count is stated because withdrawing does LESS than it sounds like: it says
                the office no longer PROPOSES a budget, not that what it proposed never
                happened. A reader who expected the PPAs to go would otherwise see nothing
                change on the plans.
              */}
              {confirming.kind === 'deactivate' && confirming.department.ppaCount > 0 ? (
                <p>
                  The {confirming.department.ppaCount} PPA
                  {confirming.department.ppaCount === 1 ? '' : 's'} it has already filed are
                  kept and stay on the plans they belong to — withdrawing a department is not a
                  statement that its budget was never proposed.
                </p>
              ) : null}

              {confirming.kind === 'activate' ? (
                <p>
                  Refused if the unit has been enrolled again meanwhile — a withdrawal frees it.
                </p>
              ) : null}

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<BudgetDepartmentListItem>[] = [
  {
    key: 'orgUnitName',
    header: 'Department',
    width: 280,
    flex: true,
    sortable: true,
    render: (department) => <TextCell value={department.orgUnitName} />,
  },
  {
    key: 'orgUnitTypeName',
    header: 'Tier',
    width: 160,
    summary: true,
    /*
      The unit's TIER, and it is gated: it belongs to the org-unit-types module, which this
      reader may not hold. The department name beside it is not gated — a reader of this
      roster is entitled to the name of what they enrolled.
    */
    permission: 'org-unit-types.read',
    render: (department) => <TextCell value={department.orgUnitTypeName} />,
  },
  {
    key: 'orgUnitCode',
    header: 'Unit code',
    width: 140,
    summary: true,
    // A PATH through the chart (`0001-2-3`), so mono and never numeric — it is an identifier,
    // and nothing in it should dim.
    permission: 'org-units.read',
    render: (department) => <TextCell value={department.orgUnitCode} mono />,
  },
  {
    key: 'ppaCount',
    header: 'PPAs filed',
    width: 110,
    align: 'right',
    /*
      NOT sortable: the server's allow-list deliberately omits it, because ordering a page by
      a correlated count makes the database compute one per row before it can page. A header
      that offered the sort would fall back silently, which reads as a broken control.
    */
    summary: true,
    permission: 'budget-plans.read',
    render: (department) => <TextCell value={String(department.ppaCount)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 140,
    status: true,
    render: (department) => {
      /*
        TWO different absences, and collapsing them would hide the one that needs acting on:
        a WITHDRAWN department was taken off the roster deliberately, while an enrolled one
        whose office has left the chart is a roster row pointing at nothing — which nobody
        chose and somebody should fix.
      */
      if (!department.isActive) {
        return <StatusText tone="inactive">Withdrawn</StatusText>;
      }

      return department.isOrgUnitActive ? (
        <StatusText tone="active">Enrolled</StatusText>
      ) : (
        <StatusText tone="blocked">Unit deleted</StatusText>
      );
    },
  },
];
